// IE Hub - Onboarding Completion Screen
// Shows success message and next steps after onboarding completion

import { useState } from 'react';
import { 
  Box, 
  Typography, 
  IconButton,
  Menu,
  MenuItem,
  MenuIcon,
  LogoutIcon
} from '../ui';
import HelpDialog from './HelpDialog';
import Help from './on-boarding/Help';
import WelcomeHero from './WelcomeHero';

interface OnboardingCompletionProps {
  userName?: string;
  onContinue: () => void;
}

export default function OnboardingCompletion({ 
  userName = "User", 
  onContinue 
}: OnboardingCompletionProps): JSX.Element {
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);

  const handleHelpOpen = () => {
    setHelpDialogOpen(true);
  };

  const handleHelpClose = () => {
    setHelpDialogOpen(false);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleLogout = () => {
    handleMobileMenuClose();
    // TODO: Implement logout logic
    console.log('Logout clicked');
  };

  return (
    <>
      {/* Mobile View */}
      <Box sx={{ 
        display: { xs: 'flex', md: 'none' },
        height: '100vh', 
        width: '100vw',
        backgroundColor: 'primary.main', // #003F44
        flexDirection: 'column',
        position: 'relative'
      }}>
        {/* Mobile Header */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '4px 12px',
          width: '100%',
          height: '60px',
          background: '#FFFFFF',
          flex: 'none',
          order: 0,
          alignSelf: 'stretch',
          flexGrow: 0
        }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box 
              component="img"
              src="/icons/logo.png"
              alt="IE Hub"
              sx={{
                width: 86,
                height: 44,
                borderRadius: 1
              }}
            />
          </Box>

          {/* Help and Hamburger Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Help handleHelpOpen={handleHelpOpen} />
            <IconButton
              onClick={handleMobileMenuOpen}
              sx={{
                color: '#033F44',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Mobile Menu */}
          <Menu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleMobileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem 
              onClick={handleLogout}
              sx={{
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.light',
                  color: 'error.contrastText'
                }
              }}
            >
              <LogoutIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>

        {/* Mobile Content */}
        <WelcomeHero 
          userName={userName}
          onContinue={onContinue}
          buttonText="Get Started →"
        />
      </Box>

      {/* Desktop View - Simplified Design */}
      <Box sx={{ 
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: 'primary.main', // #003F44
        color: 'white',
        textAlign: 'center',
        px: 3,
        py: 4,
        position: 'relative'
      }}>
        {/* Desktop Hamburger Menu - Top Right */}
        <Box sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          {/* Custom Help Link for Desktop - White Color */}
          <Typography
            component="span"
            onClick={handleHelpOpen}
            sx={{ 
              color: 'white', 
              textDecoration: 'underline', 
              fontSize: '0.875rem', 
              cursor: 'pointer',
              '&:hover': { color: 'rgba(255, 255, 255, 0.8)' } 
            }}
          >
            Help?
          </Typography>
          <IconButton
            onClick={handleMobileMenuOpen}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Desktop Menu */}
          <Menu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleMobileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem 
              onClick={handleLogout}
              sx={{
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.light',
                  color: 'error.contrastText'
                }
              }}
            >
              <LogoutIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
        
        {/* Desktop Content */}
        <WelcomeHero 
          userName={userName}
          onContinue={onContinue}
          buttonText="Get Started →"
        />
      </Box>

      {/* Help Dialog */}
      <HelpDialog 
        open={helpDialogOpen} 
        onClose={handleHelpClose} 
      />
    </>
  );
}
